import React, { FunctionComponent, MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FormRenderProps } from 'react-final-form'
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import { ValidationErrors } from 'final-form'
import { CountryCode } from 'libphonenumber-js'
import { isEmpty } from 'lodash-es'
import styled, { css, useTheme } from 'styled-components/native'

import {
  ActivityResponseData,
  Attestations,
  ConnectedServiceAttestation,
  COUNTRY_CODES,
  CountryValues,
  CustomerRedirection,
  CustomerRedirectionProps,
  DEFAULT_COUNTRY_ISO_CODE,
  ELIGIBILITY_TYPES,
  eligibilityDateOfBirthVerificationError,
  EligibilityInfo,
  extendMetadata,
  FormMetadata,
  FormStep,
  getEligibilityErrorMessage,
  getHelpEmailAddress,
  LYRA_CONFIDENTIALITY_MSG,
  PasswordPolicy,
  UiMetadata,
  useLyraIntl,
  USER_MIN_AGE_REQUIREMENT,
} from '@lyrahealth-inc/shared-app-logic'

import { BasicInfoFormValues, useBasicInfoMetadata } from './basicInfoMetadata'
import { generateCustomerRedirectionMetadata } from './customerRedirectionMetadata'
import { generateLegalAgreementsMetadata } from './legalAgreementsMetadata'
import { Divider, Link } from '../../atoms'
import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { Image } from '../../atoms/image/Image'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TextButton } from '../../atoms/textButton/TextButton'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { EligibilityFormValues } from '../../formMetadata/eligibilityMetadata'
import { useEligibilityMetadata } from '../../hooks/useEligibilityMetadata'
import { ConfidentialityNotice } from '../../molecules'
import { getAuroraAwareBackgroundColor, getFontStyles } from '../../styles'
import { mailTo, scrollToFormInputErrorWeb, scrollToTestIdWeb, ThemeType, tID } from '../../utils'
import { FormBody, FormBodyProps, FormButtonParams } from '../formBody/FormBody'
import { UICoreFormContext } from '../formBody/types'

const SubmitButtonContainer = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  marginVertical: theme.spacing['32px'],
}))

const Container = styled.View<{ theme: ThemeType; showIllustrationVariant?: boolean }>(
  ({ theme, showIllustrationVariant }) => ({
    backgroundColor: getAuroraAwareBackgroundColor(theme.isAuroraTheme, () =>
      theme.isLyraSelect && theme.isDarkMode ? theme.colors.backgroundSecondary : theme.colors.backgroundPrimary,
    ),
    width: '100%',
    padding: theme.breakpoints.isMinWidthTablet ? '52px 32px 16px 32px' : '0 0 0 0',
    ...(theme.breakpoints.isMinWidthTablet && {
      borderRadius: 16,
      width: 624,
      border: showIllustrationVariant ? 'none' : `1px solid ${theme.colors.borderDefault}`,
    }),
    ...(showIllustrationVariant && {
      paddingTop: 0,
    }),
  }),
)

const StyledTextButton = styled(TextButton)<{ theme: ThemeType }>(({ theme }) => ({
  alignSelf: 'center',
  paddingVertical: theme.spacing['24px'],
}))

const DividerStyled = styled(Divider)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['32px'],
}))

const IllustrationContainer = styled(View)({
  alignSelf: 'center',
})

const Illustration = styled(Image)({
  width: 220,
  height: 145,
})

const ConfidentialityContainer = styled.View<{ theme: ThemeType; isMobileSized: boolean }>(
  ({ theme: { spacing }, isMobileSized }) => ({
    margin: 'auto',
    ...(!isMobileSized && {
      marginTop: spacing['32px'],
    }),
  }),
)

export enum AgeCustomError {
  BELOW_MIN = 'BELOW_MIN_ERROR',
  ABOVE_MAX = 'ABOVE_MAX_ERROR',
  INVALID = 'INVALID_ERROR',
}

export type SetupAccountFormValues = EligibilityFormValues &
  BasicInfoFormValues & { customerRedirectionCheckbox?: boolean }

const DateOfBirthVerificationErrorMessage: FunctionComponent<{ careTeamEmailAddress: string }> = ({
  careTeamEmailAddress,
}) => {
  const { colors } = useTheme() as ThemeType
  return (
    <FormattedMessage
      {...eligibilityDateOfBirthVerificationError}
      values={{
        careTeamEmail: (
          <Link
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            text={careTeamEmailAddress}
            onPress={() => mailTo(careTeamEmailAddress)}
            underline={true}
            color={colors.textError}
          />
        ),
      }}
    />
  )
}

/**
 * A component that renders the Set Up Account Form.
 */
export interface SetUpAccountFormProps extends Pick<FormBodyProps, 'keyboardFormNavigationProps'> {
  eligibilityInfo: EligibilityInfo
  isCustomerInternational: boolean
  isInternationalRegistrationExperience: boolean
  customerCountryList?: CountryValues[]
  userCountryIsoCode?: CountryCode
  userCountryName?: string
  userFirstName?: string
  passwordPolicy?: PasswordPolicy
  isHealthPlanDirect?: boolean
  initialValues?: ActivityResponseData | Dict
  headerText?: string
  subHeaderText?: MessageDescriptor | string
  headerSize?: 'h2' | 'h3'
  headerUiOptions?: UiMetadata['ui:options'] & { center?: boolean }
  getPrimaryButtonText: (showEligibilityTip: boolean, hasBlockingEligibilityCheck: boolean) => React.ReactNode
  getSecondaryButtonText: (
    showEligibilityTip: boolean,
    hasBlockingEligibilityCheck: boolean,
  ) => React.ReactNode | undefined
  saveForm: ({ values }: { values: SetupAccountFormValues }) => Promise<any>
  onPrimaryButtonPress?: (
    hasValidationError?: boolean,
    handleSubmit?: FormRenderProps['handleSubmit'],
    errors?: ValidationErrors,
  ) => void | Promise<any>
  onSecondaryButtonPress?: (
    hasValidationError?: boolean,
    handleSubmit?: FormRenderProps['handleSubmit'],
  ) => void | Promise<any>
  onCountryIsoSelected?: (countryIsoCode: CountryCode) => void
  updateFormValues?: (values: SetupAccountFormValues) => void
  shouldDisplayPasswordField?: boolean
  defaultInlineErrorBannerMessage?: React.ReactNode // error displayed by default before form submission
  inlineErrorBannerMessage?: React.ReactNode // error displayed after form submission
  inlineSuccessBannerMessage?: React.ReactNode
  inlineInfoBannerMessage?: React.ReactNode
  customerRedirection?: (customerRedirectionProps: CustomerRedirectionProps) => CustomerRedirection
  customerRedirectionLinkPress?: () => void
  eligibilityCheckCount?: number
  hidePasswordChecklistOnBlur?: boolean
  formStep?: FormStep
  formContext?: Pick<UICoreFormContext, 'stringMessage' | 'onContactCNT'>
  hideNameField?: boolean
  hideDobField?: boolean
  hideCountrySelector?: boolean
  hideEligibilityInformation?: boolean
  hideDivider?: boolean
  hidePreferredName?: boolean
  hidePhoneNumber?: boolean
  showEditEmailAddressField?: boolean
  attestationContents?: Attestations | null
  hideAttestations?: boolean
  hasBlockingDateOfBirthVerification?: boolean
  formBodyCustomStyles?: FormBodyProps['formBodyCustomStyles']
  scrollContainerCustomStyles?: FormBodyProps['scrollContainerCustomStyles']
  labelContainerCustomStyle?: ViewStyle
  primaryButtonStyle?: ViewStyle
  testID?: string
  onPressAddPreferredName?: () => void
  onPressEditEmailAddress?: () => void
  isPhoneNumberRequired?: boolean
  isAutoEnrollment?: boolean
  isSSO?: boolean
  showIllustrationVariant?: boolean
  hideDependentField?: boolean
  blockInPersonCareForDependents?: boolean
  scrollViewRef?: MutableRefObject<any>
  containerStyle?: ViewStyle
  submitButtonContainerStyle?: ViewStyle
  hideLegalAgreements?: boolean
  attestationContentBaseInputStyle?: React.CSSProperties
  connectedServiceAttestation?: ConnectedServiceAttestation
}

export const SetUpAccountForm: FunctionComponent<SetUpAccountFormProps> = (props) => {
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  return (
    <Container
      testID={tID(props.testID ?? 'setUpAccountForm')}
      showIllustrationVariant={props?.showIllustrationVariant}
      style={props?.containerStyle}
    >
      <SetUpAccountFormBody
        formBodyCustomStyles={{
          submitButtonContainer: css`
            border: 0;
            border-top-width: 0;
            border-radius: 16px;
            padding-top: 0;
            box-shadow: none;
            shadow-color: transparent;
            background-color: ${isAuroraTheme
              ? 'transparent'
              : colors.components.setupAccountForm.submitButtonContainer.background};
          `,
        }}
        scrollContainerCustomStyles={{
          formBodyPageContainer: css`
            border-radius: 16px;
            padding-top: 0px;
            padding-bottom: 0px;
            background-color: ${isAuroraTheme
              ? 'transparent'
              : colors.components.setupAccountForm.formBodyPageContainer.background};
          `,
        }}
        {...props}
      />
    </Container>
  )
}

export const SetUpAccountFormBody: FunctionComponent<SetUpAccountFormProps> = ({
  isCustomerInternational,
  isInternationalRegistrationExperience,
  customerCountryList,
  userCountryIsoCode,
  userCountryName,
  passwordPolicy,
  eligibilityInfo,
  isHealthPlanDirect,
  initialValues,
  headerText,
  subHeaderText,
  headerSize,
  headerUiOptions,
  saveForm,
  onPrimaryButtonPress,
  onSecondaryButtonPress,
  onCountryIsoSelected,
  updateFormValues,
  getPrimaryButtonText,
  getSecondaryButtonText,
  shouldDisplayPasswordField = true,
  defaultInlineErrorBannerMessage,
  inlineErrorBannerMessage,
  inlineSuccessBannerMessage,
  inlineInfoBannerMessage,
  customerRedirection,
  customerRedirectionLinkPress,
  eligibilityCheckCount,
  hidePasswordChecklistOnBlur = true,
  formContext,
  hideNameField,
  hideDobField,
  hideCountrySelector,
  hideEligibilityInformation,
  hideDivider,
  hidePreferredName,
  hidePhoneNumber,
  showEditEmailAddressField,
  onPressEditEmailAddress,
  attestationContents,
  hideAttestations = false,
  testID = 'setUpAccountForm',
  keyboardFormNavigationProps,
  onPressAddPreferredName,
  hasBlockingDateOfBirthVerification = false,
  formBodyCustomStyles,
  scrollContainerCustomStyles,
  labelContainerCustomStyle,
  isPhoneNumberRequired = false,
  isAutoEnrollment,
  submitButtonContainerStyle,
  isSSO,
  primaryButtonStyle,
  showIllustrationVariant,
  hideDependentField,
  blockInPersonCareForDependents,
  scrollViewRef,
  hideLegalAgreements,
  attestationContentBaseInputStyle,
  connectedServiceAttestation,
}) => {
  const intl = useIntl()
  const { activeLanguage } = useLyraIntl()
  const { formatMessage, locale } = intl
  const {
    breakpoints: { isMobileSized },
    colors,
    fontFamilyMetadata,
  } = useTheme() as ThemeType
  const { isPreferredNameEnabled, shouldHideEligibilityTypeQuestion, isLyraForLyrians2026Enabled } =
    useContext(AppContext)
  const minAgeRequirement = USER_MIN_AGE_REQUIREMENT
  const shouldDisplayPhoneNumberField = !hidePhoneNumber
  const [showInternationalFormat, setShowInternationalFormat] = useState(false)
  const [hasFormSubmitted, setHasFormSubmitted] = useState(false)
  const [userMinAgeRequirement, setUserMinAgeRequirement] = useState(minAgeRequirement.DOMESTIC_EMPLOYEE)
  const [showUserAgeBelowMinWarningError, setShowUserAgeBelowMinWarningError] = useState(false)
  const [countryIsoCode, setCountryIsoCode] = useState(userCountryIsoCode || DEFAULT_COUNTRY_ISO_CODE)
  const [shouldShowErrorBanners, setShouldShowErrorBanners] = useState({ eligibility: false, basicInfo: false })
  const [customerRedirectionCheckboxChecked, setCustomerRedirectionCheckboxChecked] = useState(true)
  // Check if user is registering in UK with blockInPersonCareForDependents enabled
  const isUKRegistrationWithBlockingEnabled =
    countryIsoCode === COUNTRY_CODES.UNITED_KINGDOM && blockInPersonCareForDependents

  const showEligibilityTypeQuestion =
    (!isInternationalRegistrationExperience ||
      isUKRegistrationWithBlockingEnabled ||
      (isInternationalRegistrationExperience && eligibilityInfo.showEligibilityQuestionForInternationalRegistration)) &&
    !isHealthPlanDirect &&
    !shouldHideEligibilityTypeQuestion

  const { eligibilityMetadata, customFields, setEligibilityType, eligibilityType } = useEligibilityMetadata({
    eligibilityInfo,
    showInternationalFormat,
    showEligibilityErrorBanner: shouldShowErrorBanners.eligibility,
    countryIsoCode,
    blockInPersonCareForDependents,
  })
  let allCustomFields = customFields

  const showEligibilityTip = eligibilityInfo.showRegistrationTips && !isInternationalRegistrationExperience
  let employeeEligibilityError = undefined
  if (eligibilityType === ELIGIBILITY_TYPES.EMPLOYEE && eligibilityInfo.employeeEligibilityError !== undefined) {
    employeeEligibilityError = eligibilityInfo.employeeEligibilityError
  }

  const shouldDisableSubmitButton =
    eligibilityType === ELIGIBILITY_TYPES.DEPENDENT && hideDependentField && !isUKRegistrationWithBlockingEnabled

  useEffect(() => {
    setShowInternationalFormat(!!countryIsoCode && countryIsoCode !== DEFAULT_COUNTRY_ISO_CODE)
  }, [countryIsoCode])

  useEffect(() => {
    // Clear error banners
    setShouldShowErrorBanners({ eligibility: false, basicInfo: false })

    // Set age requirements
    if (eligibilityType === ELIGIBILITY_TYPES.DEPENDENT) {
      setUserMinAgeRequirement(
        showInternationalFormat ? minAgeRequirement.INTERNATIONAL_DEPENDENT : minAgeRequirement.DOMESTIC_DEPENDENT,
      )
    } else {
      setUserMinAgeRequirement(
        showInternationalFormat ? minAgeRequirement.INTERNATIONAL_EMPLOYEE : minAgeRequirement.DOMESTIC_EMPLOYEE,
      )
    }

    // Set error banners
    if (showEligibilityTip) {
      if (
        eligibilityType === ELIGIBILITY_TYPES.DEPENDENT &&
        !eligibilityInfo.checkAdultDependentEligibilityUsingDependentInfo
      ) {
        setShouldShowErrorBanners((prevState) => ({ ...prevState, eligibility: true }))
      } else {
        setShouldShowErrorBanners((prevState) => ({ ...prevState, basicInfo: true }))
      }
    }
  }, [
    eligibilityType,
    showInternationalFormat,
    showEligibilityTip,
    employeeEligibilityError,
    eligibilityInfo.checkAdultDependentEligibilityUsingDependentInfo,
    minAgeRequirement,
  ])

  const headerContent =
    headerText ||
    `${formatMessage({
      defaultMessage: "Let's set up your account",
      description: 'Registration form header for creating a new account',
    })}`

  const headerMetadata: FormMetadata = {
    schema: {
      type: 'object',
      properties: {
        header: {
          name: 'header',
          content:
            isMobileSized || headerSize === 'h3' || showIllustrationVariant
              ? `### ${headerContent}`
              : `## ${headerContent}`,
          sectionHeader: true,
        },
        ...(subHeaderText && {
          subHeader: {
            name: 'subHeader',
            content: subHeaderText,
          },
        }),
      },
    },
    uiSchema: {
      'ui:order': ['header', 'subHeader'],
      header: {
        'ui:options': {
          center: true,
          customStyles: {
            h2: {
              marginBottom: 16,
            },
            h3: {
              marginTop: 24,
              marginBottom: showIllustrationVariant ? 16 : 0,
            },
          },
          ...headerUiOptions,
        },
      },
      subHeader: {
        'ui:options': {
          customStyles: {
            p2: {
              ...getFontStyles(colors, fontFamilyMetadata).subhead.xsmall,
            },
          },
        },
      },
    },
  }

  const transformAndEvaluateErrors = (errors: Dict) => {
    if (errors.hasOwnProperty('userDOB') && errors.userDOB === AgeCustomError.BELOW_MIN) {
      setShowUserAgeBelowMinWarningError(true)
      errors.userDOB = ''
    } else {
      setShowUserAgeBelowMinWarningError(false)
    }
    return errors
  }

  const basicInfoFieldsMetadata = extendMetadata(
    headerMetadata,
    useBasicInfoMetadata({
      isCustomerInternational,
      showInternationalFormat,
      showBasicInfoErrorBanner: shouldShowErrorBanners.basicInfo,
      userMinAgeRequirement,
      intl,
      shouldDisplayPasswordField,
      shouldDisplayPhoneNumberField,
      showUserAgeBelowMinWarningError,
      hasFormSubmitted,
      colors,
      passwordPolicy,
      isMobileSized,
      hidePasswordChecklistOnBlur,
      hideNameField,
      hideDobField,
      hideCountrySelector,
      showEligibilityErrorAsWarning:
        !eligibilityInfo.hasBlockingEligibilityCheck && !hasBlockingDateOfBirthVerification,
      isPreferredNameEnabled: !hidePreferredName && isPreferredNameEnabled, // TODO: ACCOUNT-2469 Re-enable check for !eligibilityInfo.isPristineConfirmEligibility
      onPressAddPreferredName,
      showEditEmailAddressField,
      onPressEditEmailAddress,
      labelContainerCustomStyle,
      isPhoneNumberRequired,
      showOriginalValueForEditableInput: isAutoEnrollment && !isSSO,
    }),
  )

  let metadata = basicInfoFieldsMetadata

  if (showEligibilityTypeQuestion && !hideEligibilityInformation) {
    metadata = extendMetadata(metadata, eligibilityMetadata)
  }

  if (customerRedirection && customerRedirectionLinkPress) {
    const customerRedirectionMetadata = generateCustomerRedirectionMetadata(
      customerRedirection?.({
        shouldShowBanner: (eligibilityCheckCount && eligibilityCheckCount > 1) || !customerRedirectionCheckboxChecked,
        onLinkPress: customerRedirectionLinkPress,
      }),
      intl.formatMessage,
    )
    metadata = extendMetadata(metadata, customerRedirectionMetadata)
  }

  if (!hideAttestations) {
    allCustomFields = { ...allCustomFields, ...{ Divider: () => <DividerStyled color={colors.dividerSecondary} /> } }
    const legalAgreementsMetadata = generateLegalAgreementsMetadata({
      intl,
      isInternationalRegistrationExperience,
      activeLanguage,
      attestationContents,
      hideDivider,
      textColor: colors.textSecondary,
      hideLegalAgreements,
      attestationContentBaseInputStyle,
      connectedServiceAttestation,
    })
    metadata = extendMetadata(metadata, legalAgreementsMetadata)
  }

  const { uiSchema, schema } = metadata

  const handleScrollToEnd = useCallback(() => {
    if (IS_WEB) {
      scrollToTestIdWeb(`${testID}-primaryButton`)
    } else {
      scrollViewRef?.current?.scrollToEnd({ animated: true })
    }
  }, [testID, scrollViewRef])

  const onSubmitFormRef = useRef<() => void | Promise<void>>(() => {})
  const setExternalLoadingRef = useRef<((loading: boolean) => void) | null>(null)

  const SubmitButton = ({ handleSubmit, loading, errors }: FormButtonParams) => {
    const submitButtonText = getPrimaryButtonText(!!showEligibilityTip, !!eligibilityInfo.hasBlockingEligibilityCheck)
    const secondaryButtonText = getSecondaryButtonText(
      !!showEligibilityTip,
      !!eligibilityInfo.hasBlockingEligibilityCheck,
    )

    const [isPrimaryButtonLoading, setIsPrimaryButtonLoading] = useState(false)
    const [isSecondaryButtonLoading, setIsSecondaryButtonLoading] = useState(false)

    // Store the primary loading setter in ref to access externally
    setExternalLoadingRef.current = setIsPrimaryButtonLoading

    onSubmitFormRef.current = async () => {
      if (onPrimaryButtonPress) {
        setIsPrimaryButtonLoading(true)
        try {
          const formErrors = isEmpty(errors)
            ? undefined
            : {
                ...errors,
                ...(errors.password && { password: formatMessage(errors.password) }),
                ...(errors.userDOB && {
                  userDOB: showUserAgeBelowMinWarningError ? AgeCustomError.BELOW_MIN : errors.userDOB,
                }),
              }
          await onPrimaryButtonPress(!isEmpty(errors), handleSubmit, formErrors)
        } finally {
          setIsPrimaryButtonLoading(false)
          if (
            !isEmpty(errors) ||
            !showEligibilityTip ||
            (showEligibilityTip && eligibilityInfo.hasBlockingEligibilityCheck)
          ) {
            scrollToFormInputErrorWeb()
          }
        }
      } else {
        await handleSubmit()
      }
      setHasFormSubmitted(true)
    }

    const handleSecondaryButtonOnPress = async () => {
      if (onSecondaryButtonPress) {
        setIsSecondaryButtonLoading(true)
        try {
          await onSecondaryButtonPress(!isEmpty(errors), handleSubmit)
        } finally {
          setIsSecondaryButtonLoading(false)
          scrollToFormInputErrorWeb()
        }
      }
    }

    return (
      <>
        <ConfidentialityContainer isMobileSized={isMobileSized}>
          <ConfidentialityNotice
            customStyle={isMobileSized ? { marginBottom: 0 } : undefined}
            text={<FormattedMessage {...LYRA_CONFIDENTIALITY_MSG} />}
            accessibilityLabel={formatMessage(LYRA_CONFIDENTIALITY_MSG)}
          />
        </ConfidentialityContainer>
        <SubmitButtonContainer
          style={{
            ...submitButtonContainerStyle,
          }}
        >
          <PrimaryButton
            text={submitButtonText}
            onPress={() => onSubmitFormRef.current()}
            testID={tID(`${testID}-primaryButton`)}
            disabled={loading || isPrimaryButtonLoading || shouldDisableSubmitButton}
            loading={loading || isPrimaryButtonLoading}
            size={ButtonSize.LARGE}
            fullWidth={isMobileSized}
            style={{ ...(!isMobileSized && { ...primaryButtonStyle, alignSelf: 'center' }) }}
          />
          {secondaryButtonText && !loading && onSecondaryButtonPress && (
            <StyledTextButton
              text={secondaryButtonText}
              onPress={handleSecondaryButtonOnPress}
              testID={tID(`${testID}-textButton`)}
              loading={isSecondaryButtonLoading}
              disabled={isPrimaryButtonLoading}
            />
          )}
        </SubmitButtonContainer>
      </>
    )
  }

  let errorBannerMessage: React.ReactNode | null = null
  if (!hasFormSubmitted && defaultInlineErrorBannerMessage) {
    errorBannerMessage = defaultInlineErrorBannerMessage
  } else if (inlineErrorBannerMessage) {
    errorBannerMessage = inlineErrorBannerMessage
  } else if (hasBlockingDateOfBirthVerification) {
    errorBannerMessage = (
      <DateOfBirthVerificationErrorMessage
        careTeamEmailAddress={getHelpEmailAddress({
          customerName: eligibilityInfo.customerName,
          isInternationalUser: showInternationalFormat,
          isLyraForLyrians2026Enabled: isLyraForLyrians2026Enabled ?? false,
        })}
      />
    )
  } else {
    errorBannerMessage = getEligibilityErrorMessage({
      formatMessage,
      eligibilityInfo,
      eligibilityType,
      isHealthPlanDirect,
      customEmployeeEligibilityError: employeeEligibilityError,
      eligibilityContinueLink: (text: string) => (
        <Link
          testID={tID('EligibilityErrorBannerLink')}
          onPress={() => {
            setExternalLoadingRef.current?.(true)
            onSubmitFormRef.current()
            handleScrollToEnd()
          }}
          color={colors.components.inlineWarningBanner.text}
          text={text}
          underline
        />
      ),
    })
  }

  const onFormChange = ({ values }: { values: SetupAccountFormValues }) => {
    const { countryIsoCode: selectedCountryIsoCode, eligibilityType } = values
    if (selectedCountryIsoCode && (selectedCountryIsoCode !== countryIsoCode || !userCountryName)) {
      setCountryIsoCode(selectedCountryIsoCode)
      onCountryIsoSelected?.(selectedCountryIsoCode)
    }
    if (eligibilityType) {
      setEligibilityType(eligibilityType)
    }
    if (
      typeof values.customerRedirectionCheckbox === 'boolean' &&
      customerRedirectionCheckboxChecked !== values.customerRedirectionCheckbox
    ) {
      setCustomerRedirectionCheckboxChecked(values.customerRedirectionCheckbox)
    }
    updateFormValues && updateFormValues(values)
  }

  const onSaveForm = async ({ values }: { values: SetupAccountFormValues }) => {
    return await saveForm({ values })
  }

  return (
    !!schema && (
      <>
        {showIllustrationVariant && (
          <IllustrationContainer>
            {
              <Illustration
                source={require('../../assets/handAndKey.png')}
                contentFit='contain'
                accessibilityIgnoresInvertColors
              />
            }
          </IllustrationContainer>
        )}
        <FormBody
          enableTranslations
          intl={intl}
          key={testID}
          name={testID}
          schema={schema}
          uiSchema={uiSchema}
          inlineErrorBannerMessage={errorBannerMessage}
          inlineSuccessBannerMessage={inlineSuccessBannerMessage}
          inlineInfoBannerMessage={inlineInfoBannerMessage}
          inlineWarningBannerMessage={errorBannerMessage}
          onFormChange={onFormChange}
          saveForm={onSaveForm}
          transformAndEvaluateErrors={transformAndEvaluateErrors}
          formButton={(props) => <SubmitButton {...props} />}
          customerCountryList={customerCountryList}
          userCountryIsoCode={userCountryIsoCode}
          userCountryName={userCountryName}
          passwordPolicy={passwordPolicy}
          showAsteriskForRequiredFields={true}
          checkPristineOnFormChange={false}
          locale={locale}
          formContext={formContext}
          formBodyCustomStyles={formBodyCustomStyles}
          scrollContainerCustomStyles={scrollContainerCustomStyles}
          customFields={allCustomFields}
          initialValues={initialValues}
          inputAccessoryViewID={testID}
          returnKeyType='done'
          keyboardFormNavigationProps={keyboardFormNavigationProps}
        />
      </>
    )
  )
}
